import { useEffect, useMemo } from 'react';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { DashboardWrapper, RotatedAxisTick } from '../../components';
import { PERMISSIONS } from '../../constants';
import useDashboardContext from '../../hooks/useDashboardContext';
import { getChainId } from '../../lib';
import { getDatesInRange } from '../../utils';


const RANK_CUSTOMERS_LIMIT = 10;

const chartMargin = { top: 10, right: 16, bottom: 10, left: 16 };
const yAxisDomain = ['auto', 'auto'];
const tooltipItemStyle = { color: '#333', fontSize: '14px' };
const tooltipContentStyle = {
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  borderRadius: '5px',
  padding: '8px 16px',
};
const legendWrapperStyle = { top: -10, left: 20 };
const barRadius = [5, 5, 0, 0];

const tooltipFormatter = (value) => `${value} pontos`;
const tooltipLabelFormatter = (label) => label;
const legendFormatter = () => `Pontos Adicionados`;
const xAxisTickFormatter = (tick) => tick.substring(0, 5);
const yAxisTickFormatter = (value) => `${value} pontos`;

export const AditionPointsChart = () => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const {
    from, to, chain, stationIds,
    pointsSummaryData, pointsSummaryLoading,
    setPointsSummaryData, setPointsSummaryLoading
  } = useDashboardContext();
  const notify = useNotify();
  const chainId = permissions && permissions.includes(PERMISSIONS.ADMIN) ? chain : getChainId();

  useEffect(() => {
    if (pointsSummaryLoading) return;

    const filter = { from, to, limit: RANK_CUSTOMERS_LIMIT };
    if (stationIds && stationIds.length > 0) { filter.placeIds = stationIds; }

    if (permissions && permissions.includes(PERMISSIONS.ADITION_POINTS)) {
      setPointsSummaryLoading(true);
      dataProvider.getList('dashboard/points-summary', { chainId, filter })
        .then(response => {
          setPointsSummaryData(response.data[0]);
        })
        .catch((e) => {
          notify('Erro ao carregar dados de pontos', { type: 'warning' });
          setPointsSummaryData(null);
        })
        .finally(() => {
          setPointsSummaryLoading(false);
        });
    }
  }, [permissions, from, to, chain, stationIds]);

  const chartData = useMemo(() => {
    if (!pointsSummaryData?.perDay) return null;

    const data = [];
    getDatesInRange(from, to).forEach(date =>
      data.push({ id: date, aditionPoints: 0 })
    );

    pointsSummaryData.perDay.forEach(point => {
      const currDate = data.find(chartDate => chartDate.id === point.date);
      if (currDate) {
        currDate.aditionPoints = +point.aditionPoints || 0;
      }
    });

    return data;
  }, [pointsSummaryData, from, to]);

  const chartContent = useMemo(() => (
    <BarChart data={chartData || []} margin={chartMargin}>
      <XAxis
        dataKey="id"
        tick={<RotatedAxisTick fontSize={12} />}
        height={60}
        tickFormatter={xAxisTickFormatter}
      />
      <YAxis
        width={50}
        domain={yAxisDomain}
        type="number"
        tickFormatter={yAxisTickFormatter}
      />
      <Tooltip
        formatter={tooltipFormatter}
        labelFormatter={tooltipLabelFormatter}
        itemStyle={tooltipItemStyle}
        contentStyle={tooltipContentStyle}
      />
      <CartesianGrid strokeDasharray="3 3" stroke="#DFE2E6" />
      <Legend
        layout="horizontal"
        verticalAlign="top"
        wrapperStyle={legendWrapperStyle}
        formatter={legendFormatter}
      />
      <Bar
        dataKey="aditionPoints"
        fill="url(#gradient)"
        barSize={50}
        name="Pontos Adicionados Manualmente"
        radius={barRadius}
        isAnimationActive={true}
      />
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#82ca9d" stopOpacity={1} />
          <stop offset="100%" stopColor="#61dafb" stopOpacity={1} />
        </linearGradient>
      </defs>
    </BarChart>
  ), [chartData]);

  return (
    <DashboardWrapper
      permission={PERMISSIONS.ADITION_POINTS}
      title="Pontos Adicionados Manualmente"
      width="45%"
      link="/report-added-points"
      count={chartData?.length || 0}
      loading={!pointsSummaryData && pointsSummaryLoading}
      content={chartContent}
    />
  );
};
